'use client';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogTrigger,
} from '@src/v3/components/coreUI/dialog';
import Loader from '@src/v3/components/loader';
import {
  UserTypeStep,
  OtpStep,
  UserDetailsStep,
  WorkDetailsStep,
  EmailVerificationStep,
  UpdateEmailStep,
  UpdatePhoneStep,
  RegisterModalProps,
  RegisterStep,
  UserType,
  RegisterFormValues,
} from './components';
import PhoneStep from '@src/v3/components/loginModal/components/PhoneStep';
import {
  sendSignupOtp,
  verifySignupOtp,
  completeRenterSignup,
  sendSignupEmailOtp,
  verifySignupEmailOtp,
  completeAgentSignup,
  completeLandlordSignup,
  loginWithGoogle,
  loginWithApple,
  verifyOAuthOtp,
  checkEmailExist,
  sendLoginPhoneOtp,
  editRenter,
  editAgent,
  editLandlord,
  sendBasicPhoneOtp,
  getUserProfile,
} from '@src/v3/services/auth';
import { setItem, getItem, getUserId } from '@src/v3/utils/localStorage';
import {
  ZEME_USER_DATA,
  ZEME_TEMP_SESSION_TOKEN,
  ZEME_SIGNUP_IN_PROGRESS,
  ZEME_VERIFIED_PHONE,
} from '@src/v3/constants/localStorage';
import { showErrorMsg } from '@src/v3/utils/notifications';
import { useRouter, usePathname } from 'next/navigation';
import { APP_ROUTE } from '@src/v3/constants/routes';
import {
  ERROR_MESSAGES,
  VALIDATION_MESSAGES,
} from '@src/v3/constants/messages';
import {
  formatPhoneForApi,
  transformRenterProfile,
} from '@src/v3/utils/helper';
import {
  ZEME_USERS_ROLE,
  REGISTER_STEPS,
  LOGIN_STEPS,
} from '@src/v3/constants/configs';
import useProfile from '@src/v3/hooks/useProfile';
import { jwtDecode } from 'jwt-decode';
import { checkRenterInvite } from '@src/v3/services/application';

const RegisterModal: React.FC<RegisterModalProps> = ({
  isOpen,
  onClose,
  trigger,
  onOpenLoginModal,
  inviteToken,
  defaultUserType,
  skipUserTypeSelection = false,
  redirectUrl,
  onSuccess,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const { setIsUserLoggedIn, fetchMyProfile } = useProfile();
  const [step, setStep] = useState<RegisterStep>(
    skipUserTypeSelection && defaultUserType
      ? REGISTER_STEPS.PHONE
      : REGISTER_STEPS.USER_TYPE
  );
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [selectedUserType, setSelectedUserType] = useState<
    UserType | undefined
  >(defaultUserType);
  const [isOAuthFlow, setIsOAuthFlow] = useState<boolean>(false);
  // Track if user exists for correct success message (login vs registration)
  const [userDetailsData, setUserDetailsData] =
    useState<RegisterFormValues | null>(null);
  const [workDetailsData, setWorkDetailsData] =
    useState<RegisterFormValues | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOtpLoading, setIsOtpLoading] = useState<boolean>(false);
  const [isUserDetailsLoading, setIsUserDetailsLoading] =
    useState<boolean>(false);
  const [isWorkDetailsLoading, setIsWorkDetailsLoading] =
    useState<boolean>(false);
  const [isEmailVerificationLoading, setIsEmailVerificationLoading] =
    useState<boolean>(false);
  const [isUpdateEmailLoading, setIsUpdateEmailLoading] =
    useState<boolean>(false);
  const [isUpdatePhoneLoading, setIsUpdatePhoneLoading] =
    useState<boolean>(false);
  const [phoneResendTimer, setPhoneResendTimer] = useState<number>(0);
  const [emailResendTimer, setEmailResendTimer] = useState<number>(0);
  const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);
  const [userTypeError, setUserTypeError] = useState<string>('');
  const [userDetailsError, setUserDetailsError] = useState<string>('');
  const [workDetailsError, setWorkDetailsError] = useState<string>('');
  const [isVerifiedPhone, setIsVerifiedPhone] = useState<boolean>(false);
  const [savedCropState, setSavedCropState] = useState<{
    scale: number;
    crop: { x: number; y: number };
  } | null>(null);
  const resetModalState = () => {
    setStep(REGISTER_STEPS.USER_TYPE);
    setPhone('');
    setEmail('');
    setSelectedUserType(defaultUserType);
    setIsLoading(false);
    setIsOtpLoading(false);
    setIsUserDetailsLoading(false);
    setIsWorkDetailsLoading(false);
    setIsEmailVerificationLoading(false);
    setIsUpdateEmailLoading(false);
    setPhoneResendTimer(0);
    setEmailResendTimer(0);
    setUserDetailsData(null);
    setWorkDetailsData(null);
    setUserTypeError('');
    setUserDetailsError('');
    setWorkDetailsError('');
    setIsOAuthFlow(false);
    setSavedCropState(null);
  };

  const handleModalClose = () => {
    const signupInProgress =
      localStorage.getItem(ZEME_SIGNUP_IN_PROGRESS) === 'true';
    if (signupInProgress) {
      localStorage.removeItem(ZEME_USER_DATA);
      localStorage.removeItem(ZEME_SIGNUP_IN_PROGRESS);
      setIsUserLoggedIn?.(false);
    }
    localStorage.removeItem(ZEME_TEMP_SESSION_TOKEN);
    localStorage.removeItem(ZEME_VERIFIED_PHONE);
    resetModalState();
    onClose();
  };

  const useCountdownTimer = (
    timer: number,
    // eslint-disable-next-line no-unused-vars
    setTimer: (value: number | ((prev: number) => number)) => void
  ) => {
    useEffect(() => {
      let interval: NodeJS.Timeout;
      if (timer > 0) {
        interval = setInterval(() => {
          setTimer((prev: number) => prev - 1);
        }, 1000);
      }
      return () => clearInterval(interval);
    }, [timer, setTimer]);
  };

  useCountdownTimer(phoneResendTimer, setPhoneResendTimer);
  useCountdownTimer(emailResendTimer, setEmailResendTimer);

  const canResendPhone = phoneResendTimer === 0;
  const canResendEmail = emailResendTimer === 0;
  const PHONE_RESEND_TIMER = 120;
  const EMAIL_RESEND_TIMER = 120;

  // inside RegisterModal
  useEffect(() => {
    const signupInProgress =
      localStorage.getItem(ZEME_SIGNUP_IN_PROGRESS) === 'true';
    const userData = localStorage.getItem(ZEME_USER_DATA);

    if (signupInProgress && userData) {
      localStorage.removeItem(ZEME_USER_DATA);
      localStorage.removeItem(ZEME_SIGNUP_IN_PROGRESS);
      setIsUserLoggedIn?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure phone is set when modal opens with verified phone
  useEffect(() => {
    if (isOpen) {
      const currentVerifiedPhone =
        (getItem(ZEME_VERIFIED_PHONE) as string) || '';
      if (currentVerifiedPhone && !phone) {
        setPhone(currentVerifiedPhone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleUserTypeSelect = (userType: UserType) => {
    setSelectedUserType(userType);
  };

  const handleStepChange = (newStep: RegisterStep) => {
    // Re-check verified phone in case it was set after component mount
    const currentVerifiedPhone = getItem(ZEME_VERIFIED_PHONE) as string;
    // If phone is verified and user tries to go to PHONE or OTP steps, skip them
    if (
      currentVerifiedPhone &&
      (newStep === REGISTER_STEPS.PHONE || newStep === REGISTER_STEPS.OTP)
    ) {
      // Skip phone/OTP steps since phone is already verified
      // Make sure phone state is set
      if (!phone && currentVerifiedPhone) {
        setPhone(currentVerifiedPhone);
      }
      setStep(REGISTER_STEPS.USER_DETAILS);
      return;
    }
    setStep(newStep);
  };

  const handleSaveWorkDetails = (values: RegisterFormValues) => {
    setWorkDetailsData(values);
  };
  const handleApiError = (error: unknown, defaultMessage: string): void => {
    console.error(defaultMessage, error);
  };

  const sendOtpAndProceed = async (
    phoneNumber: string,
    userType: UserType
  ): Promise<void> => {
    const phoneDigits = formatPhoneForApi(phoneNumber);
    const response = await sendSignupOtp(phoneDigits, userType);
    const { status, message } = response || {};
    if (status === 'success') {
      setPhone(phoneNumber);
      handleStepChange(REGISTER_STEPS.OTP);
    } else {
      const errorMessage = message || ERROR_MESSAGES.failedToSendOtp;
      const error = new Error(errorMessage) as Error & {
        response?: { data?: { message?: string } };
      };
      error.response = { data: { message: errorMessage } };
      throw error;
    }
  };

  const handleOAuthResponse = async (
    response: Record<string, unknown>,
    userEmail: string
  ): Promise<void> => {
    // Check if email exists first (same as login modal approach)
    const { exists } = await checkEmailExist({ email: userEmail });

    // Get response data with defaults
    let is_registration_complete = true;
    if (response?.is_registration_complete !== undefined) {
      is_registration_complete = response?.is_registration_complete as boolean;
    }
    const phone_number = response?.phone_number?.toString();
    const access_token = response?.access_token;
    const verified_phone = response?.verified_sms as boolean;
    setIsVerifiedPhone(verified_phone);

    if (!verified_phone && access_token) {
      setItem(ZEME_USER_DATA, response);
      setItem(ZEME_SIGNUP_IN_PROGRESS, 'true');
    }

    const isExistingCompleteUser =
      exists === true && is_registration_complete === true;

    if (isExistingCompleteUser && phone_number) {
      // Registration is complete - go to OTP step for verification
      setEmail(userEmail);
      setPhone(phone_number);
      setIsOAuthFlow(true);

      // Send OTP to the phone number
      try {
        const phoneDigits = formatPhoneForApi(phone_number);
        if (verified_phone) {
          await sendLoginPhoneOtp(phoneDigits);
        } else {
          await sendBasicPhoneOtp(phoneDigits, ZEME_USERS_ROLE.RENTER);
        }
        handleStepChange(REGISTER_STEPS.OTP);
      } catch (error) {
        console.error('Failed to send OTP:', error);
        showErrorMsg('Failed to send OTP. Please try again.');
      }
      return;
    } else {
      if (response?.user_id) {
        const profileResponse = await getUserProfile(
          ZEME_USERS_ROLE.RENTER,
          String(response?.user_id || '')
        );
        const profile = transformRenterProfile(profileResponse);
        if (profile) {
          setUserDetailsData({
            ...userDetailsData,
            userType: ZEME_USERS_ROLE.RENTER as UserType,
            phone: profile?.generalInformation?.phoneNumber,
            code: '',
            firstName: profile?.generalInformation?.firstName,
            lastName: profile?.generalInformation?.lastName,
            middleName: profile?.generalInformation?.middleName,
            email: profile?.generalInformation?.email,
          });
        }
      }
      // Show user details overlay to fill up renter details
      setEmail(userEmail);
      setIsOAuthFlow(true);
      setItem(ZEME_SIGNUP_IN_PROGRESS, 'true');
      handleStepChange(REGISTER_STEPS.USER_DETAILS);
      return;
    }
  };

  const withLoadingState = async (
    // eslint-disable-next-line no-unused-vars
    loadingSetter: (loading: boolean) => void,
    operation: () => Promise<void>,
    errorMessage: string,
    // eslint-disable-next-line no-unused-vars
    onError?: (error: unknown) => void
  ): Promise<void> => {
    loadingSetter(true);
    try {
      await operation();
    } catch (error) {
      if (onError) {
        onError(error);
      }
      handleApiError(error, errorMessage);
      throw error; // Re-throw so calling component can handle it
    } finally {
      loadingSetter(false);
    }
  };

  const handlePhoneSubmit = async (values: RegisterFormValues) => {
    if (!selectedUserType) {
      setUserTypeError(VALIDATION_MESSAGES.selectUserType);
      return;
    }
    setUserTypeError(''); // Clear error
    await withLoadingState(
      setIsLoading,
      () => sendOtpAndProceed(values.phone, selectedUserType),
      ERROR_MESSAGES.failedToSendSignupOtp
    );
  };

  const handleOtpSubmit = async (values: RegisterFormValues): Promise<void> => {
    if (!selectedUserType) {
      throw new Error(ERROR_MESSAGES.userTypeNotSelected);
    }
    setIsOtpLoading(true);
    try {
      const phoneDigits = formatPhoneForApi(values.phone);
      let response;
      if (isOAuthFlow) {
        response = await verifyOAuthOtp(phoneDigits, values.code);
      } else {
        response = await verifySignupOtp(
          phoneDigits,
          values.code,
          selectedUserType
        );
      }

      // Only proceed if API call was successful
      // Explicitly check for success - navigation only happens if API returns success
      const isSuccess =
        response?.status === 'success' || response?.access_token;

      if (!isSuccess) {
        // API call did not succeed - extract error message and throw
        const errorMessage =
          response?.message ||
          response?.error ||
          response?.detail ||
          ERROR_MESSAGES.otpCodeInvalid;
        const error = new Error(errorMessage) as Error & {
          response?: { data?: { message?: string } };
        };
        error.response = { data: { message: errorMessage } };
        throw error;
      }

      // Handle OAuth flow - requires access_token
      if (isOAuthFlow && response?.access_token) {
        setItem(ZEME_USER_DATA, response);
        localStorage.removeItem(ZEME_SIGNUP_IN_PROGRESS);

        // Update authentication state in context
        if (setIsUserLoggedIn) {
          setIsUserLoggedIn(true);
        }
        // Fetch user profile to update header
        if (fetchMyProfile) {
          await fetchMyProfile();
        }

        let hasInvite = false;
        try {
          const inviteResponse = await checkRenterInvite();
          hasInvite = inviteResponse?.has_invite || false;
        } catch (inviteError) {
          console.error('Failed to check renter invite:', inviteError);
        }

        handleModalClose();

        // Small delay to ensure profile state is updated before navigation
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Handle redirect logic
        if (onSuccess) {
          onSuccess();
        } else if (redirectUrl) {
          handleModalClose();
          router.push(redirectUrl);
        } else if (inviteToken) {
          window?.location?.reload();
        } else if (hasInvite) {
          router.push(APP_ROUTE.RENTER_MY_APPLICATION);
        } else if (pathname === APP_ROUTE.EXPLORE) {
          window?.location?.reload();
        } else {
          router.push(APP_ROUTE.EXPLORE);
        }
        return;
      }

      // Handle normal signup flow - OTP verified successfully
      // Note: access_token might not be present yet during signup, it will be set after completing user details
      if (!isOAuthFlow && isSuccess) {
        // If access_token is present, save it and update auth state
        if (response?.access_token) {
          setItem(ZEME_USER_DATA, response);
          if (setIsUserLoggedIn) {
            setIsUserLoggedIn(true);
          }
          if (fetchMyProfile) {
            fetchMyProfile();
          }
        }

        // Proceed to user details step after successful OTP verification
        handleStepChange(REGISTER_STEPS.USER_DETAILS);
      }
    } catch (error) {
      // Ensure error is properly formatted for display
      const apiError = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      // Re-throw the error so OtpStep can catch and display it
      throw apiError;
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handlePhoneResend = async () => {
    if (!canResendPhone) return;

    await withLoadingState(
      setIsLoading,
      async () => {
        const phoneDigits = formatPhoneForApi(phone);

        if (isOAuthFlow) {
          if (!isVerifiedPhone) {
            await sendBasicPhoneOtp(phoneDigits, ZEME_USERS_ROLE.RENTER);
            setPhoneResendTimer(PHONE_RESEND_TIMER);
            return;
          }
          // For OAuth flow, use sendLoginPhoneOtp
          const response = await sendLoginPhoneOtp(phoneDigits);
          if (response?.session_token) {
            setItem(ZEME_TEMP_SESSION_TOKEN, response.session_token);
          }
        } else {
          // Normal signup flow
          if (!selectedUserType) {
            throw new Error(VALIDATION_MESSAGES.selectUserType);
          }
          await sendBasicPhoneOtp(phoneDigits, selectedUserType);
        }
        setPhoneResendTimer(PHONE_RESEND_TIMER);
      },
      ERROR_MESSAGES.failedToResendOtp
    );
  };

  const handleUserDetailsSubmit = async (values: RegisterFormValues) => {
    if (isUserDetailsLoading) {
      return;
    }
    setUserDetailsError(''); // Clear previous error
    if (!values.firstName || !values.lastName || !values.email) {
      setUserDetailsError(VALIDATION_MESSAGES.fillAllRequiredFields);
      throw new Error(VALIDATION_MESSAGES.fillAllRequiredFields);
    }
    if (!isOAuthFlow && !phone) {
      setUserDetailsError(ERROR_MESSAGES.phoneNumberNotFound);
      throw new Error(ERROR_MESSAGES.phoneNumberNotFound);
    }
    setIsUserDetailsLoading(true);
    try {
      if (isOAuthFlow) {
        if (!values.phone) {
          setUserDetailsError(ERROR_MESSAGES.phoneNumberRequiredForOAuth);
          setIsUserDetailsLoading(false);
          throw new Error(ERROR_MESSAGES.phoneNumberRequiredForOAuth);
        }

        // For OAuth renter signup, first update general information with name and phone
        if (selectedUserType === ZEME_USERS_ROLE.RENTER) {
          const userId = getUserId();
          if (!userId) {
            setUserDetailsError(
              'Something went wrong. Kindly refresh the page and try again.'
            );
            setIsUserDetailsLoading(false);
            throw new Error('User ID not found. Please try again.');
          }

          const phoneDigits = formatPhoneForApi(values.phone);
          const generalInformation: Record<string, unknown> = {
            first_name: values.firstName,
            middle_name: values.middleName || '',
            last_name: values.lastName,
            phone_number: phoneDigits,
          };

          // Patch renter with general information
          await editRenter(
            {
              general_information: generalInformation,
            },
            userId
          );
        }

        // Then send OTP
        await sendBasicPhoneOtp(
          formatPhoneForApi(values.phone),
          selectedUserType!
        );
        setPhone(values.phone);
        handleStepChange(REGISTER_STEPS.OTP);
      } else {
        const { exists } = await checkEmailExist({
          email: values.email,
        });
        if (exists) {
          setUserDetailsError(ERROR_MESSAGES.emailAlreadyExists);
          return;
        }
        setEmail(values.email);
        setUserDetailsData(values);
        switch (selectedUserType) {
          case ZEME_USERS_ROLE.AGENT:
          case ZEME_USERS_ROLE.LANDLORD:
            handleStepChange(REGISTER_STEPS.WORK_DETAILS);
            break;
          default:
            const phoneDigits = formatPhoneForApi(phone);
            const renterSignupResponse = await completeRenterSignup(
              values.firstName,
              values.middleName || '',
              values.lastName,
              values.email,
              phoneDigits
            );

            // Store the token from the response if available
            if (renterSignupResponse?.access_token) {
              setItem(ZEME_USER_DATA, renterSignupResponse);
              setItem(ZEME_SIGNUP_IN_PROGRESS, 'true');
            }

            handleStepChange(REGISTER_STEPS.EMAIL_VERIFICATION);
            break;
        }
      }
    } catch (error) {
      const errorMessage =
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        ERROR_MESSAGES.failedToCompleteSignup;
      setUserDetailsError(errorMessage);
      handleApiError(error, ERROR_MESSAGES.failedToCompleteSignup);
      throw error; // Re-throw so component can handle it
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  const handleWorkDetailsSubmit = async (values: RegisterFormValues) => {
    if (isWorkDetailsLoading) {
      return;
    }
    setWorkDetailsError(''); // Clear previous error
    if (!values.companyName || !values.companyAddressLine1) {
      setWorkDetailsError(VALIDATION_MESSAGES.fillAllRequiredFields);
      throw new Error(VALIDATION_MESSAGES.fillAllRequiredFields);
    }
    if (values.companyAddressLine1 && !values.companyAddressPlaceId) {
      setWorkDetailsError(VALIDATION_MESSAGES.selectValidAddress);
      throw new Error(VALIDATION_MESSAGES.selectValidAddress);
    }
    if (!userDetailsData) {
      setWorkDetailsError(ERROR_MESSAGES.userDetailsNotFound);
      throw new Error(ERROR_MESSAGES.userDetailsNotFound);
    }
    if (!phone) {
      setWorkDetailsError(ERROR_MESSAGES.phoneNumberNotFound);
      throw new Error(ERROR_MESSAGES.phoneNumberNotFound);
    }
    setIsWorkDetailsLoading(true);
    try {
      setWorkDetailsData(values);
      const phoneDigits = formatPhoneForApi(phone);

      let completeSignupResponse;
      if (selectedUserType === ZEME_USERS_ROLE.LANDLORD) {
        completeSignupResponse = await completeLandlordSignup(
          userDetailsData.firstName || '',
          userDetailsData.lastName || '',
          userDetailsData.email || '',
          phoneDigits,
          values.companyName || '',
          values.companyAddressLine1 || '',
          userDetailsData.middleName,
          values.companyAddressLine2,
          values.companyAddressPlaceId,
          values.companyPhoto
        );
      } else {
        completeSignupResponse = await completeAgentSignup(
          userDetailsData.firstName || '',
          userDetailsData.lastName || '',
          userDetailsData.email || '',
          phoneDigits,
          values.companyName || '',
          values.companyAddressLine1 || '',
          userDetailsData.middleName,
          userDetailsData.licenseNumber,
          values.companyAddressLine2,
          values.companyAddressPlaceId,
          values.companyPhoto
        );
      }

      // Store the token from the response if available
      if (completeSignupResponse?.access_token) {
        setItem(ZEME_USER_DATA, completeSignupResponse);
        setItem(ZEME_SIGNUP_IN_PROGRESS, 'true');
      }

      const agentEmail = userDetailsData.email || email;
      // if (agentEmail) {
      //   await sendSignupEmailOtp(agentEmail);
      // }
      setEmail(agentEmail);
      handleStepChange(REGISTER_STEPS.EMAIL_VERIFICATION);
    } catch (error) {
      const errorMessage =
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        ERROR_MESSAGES.failedToCompleteAgentSignup;
      setWorkDetailsError(errorMessage);
      handleApiError(error, ERROR_MESSAGES.failedToCompleteAgentSignup);
      throw error; // Re-throw so component can handle it
    } finally {
      setIsWorkDetailsLoading(false);
    }
  };

  const handleEmailVerificationSubmit = async (code: string): Promise<void> => {
    if (!email) {
      showErrorMsg(ERROR_MESSAGES.emailNotFound);
    }
    if (!code) {
      showErrorMsg(ERROR_MESSAGES.verificationCodeRequired);
    }
    setIsEmailVerificationLoading(true);
    try {
      const response = await verifySignupEmailOtp(email, code);

      // Check if response contains access_token and save it
      if (response?.access_token) {
        setItem(ZEME_USER_DATA, response);
        localStorage.removeItem(ZEME_SIGNUP_IN_PROGRESS);
        // Update authentication state in context
        if (setIsUserLoggedIn) {
          setIsUserLoggedIn(true);
        }

        // Fetch user profile to update header
        if (fetchMyProfile) {
          await fetchMyProfile();
        }
      }

      if (
        selectedUserType === ZEME_USERS_ROLE.AGENT ||
        selectedUserType === ZEME_USERS_ROLE.LANDLORD
      ) {
        // Agents and landlords go directly to verification (subscription is auto-assigned by backend)
        router.push(APP_ROUTE.VERIFICATION_REQUIRED);
      } else {
        // Check for renter invite
        let hasInvite = false;
        try {
          const inviteResponse = await checkRenterInvite();
          hasInvite = inviteResponse?.has_invite || false;
        } catch (inviteError) {
          console.error('Failed to check renter invite:', inviteError);
        }
        // Handle redirect logic for renters
        if (onSuccess) {
          onSuccess();
        } else if (redirectUrl) {
          handleModalClose();
          router.push(redirectUrl);
        } else if (inviteToken) {
          window?.location?.reload();
        } else if (hasInvite) {
          router.push(APP_ROUTE.RENTER_MY_APPLICATION);
        } else if (pathname === APP_ROUTE.EXPLORE) {
          window?.location?.reload();
        } else {
          router.push(APP_ROUTE.EXPLORE);
        }
      }
    } catch (error) {
      console.error(error);
      throw error; // Re-throw so EmailVerificationStep can display the error
    } finally {
      setIsEmailVerificationLoading(false);
    }
  };

  const handleEmailResend = async () => {
    if (!canResendEmail || !email) return;
    await withLoadingState(
      setIsEmailVerificationLoading,
      async () => {
        await sendSignupEmailOtp(email);
        setEmailResendTimer(EMAIL_RESEND_TIMER);
      },
      ERROR_MESSAGES.failedToResendVerificationEmail
    );
  };

  const handleUpdateEmailSubmit = async (
    values: RegisterFormValues
  ): Promise<void> => {
    if (!values.email) {
      return;
    }
    if (!phone) {
      return;
    }
    setIsUpdateEmailLoading(true);
    try {
      const userId = getUserId();
      if (!userId) {
        showErrorMsg(
          'Something went wrong. Kindly refresh the page and try again.'
        );
        setIsUpdateEmailLoading(false);
        return;
      }

      // Update email using edit APIs based on user type
      // Note: Edit APIs don't return a new token, we use the existing token from complete signup
      switch (selectedUserType) {
        case ZEME_USERS_ROLE.AGENT:
          if (!userDetailsData) {
            setIsUpdateEmailLoading(false);
            return;
          }

          // Call editAgent to update the email
          await editAgent(
            {
              general_information: {
                email: values.email,
              },
            },
            userId
          );
          break;

        case ZEME_USERS_ROLE.LANDLORD:
          if (!userDetailsData) {
            setIsUpdateEmailLoading(false);
            return;
          }

          // Call editLandlord to update the email
          await editLandlord(
            {
              general_information: {
                email: values.email,
              },
            },
            userId
          );
          break;

        default:
          if (!userDetailsData) {
            setIsUpdateEmailLoading(false);
            return;
          }

          // Call editRenter to update the email
          await editRenter(
            {
              general_information: {
                email: values.email,
              },
            },
            userId
          );
          break;
      }

      // Send OTP to the new email using the same API as resend code
      await sendSignupEmailOtp(values.email);

      // Update email state and user details
      setEmail(values.email);
      setUserDetailsData({
        ...userDetailsData,
        email: values.email,
      });

      handleStepChange(REGISTER_STEPS.EMAIL_VERIFICATION);
    } catch (error) {
      handleApiError(
        error,
        ERROR_MESSAGES.failedToUpdateEmailAndSendVerification
      );
      throw error;
    } finally {
      setIsUpdateEmailLoading(false);
    }
  };

  const handleUpdatePhoneSubmit = async (
    values: RegisterFormValues
  ): Promise<void> => {
    if (!values.phone) {
      return;
    }
    setIsUpdatePhoneLoading(true);
    try {
      const phoneDigits = formatPhoneForApi(values.phone);

      // If we are in OAuth flow, we might need to update the phone number in the backend
      if (isOAuthFlow) {
        const userId = getUserId();
        if (userId) {
          // Update phone number
          await editRenter(
            {
              general_information: {
                phone_number: phoneDigits,
              },
            },
            userId
          );
        }

        const response = await sendBasicPhoneOtp(
          phoneDigits,
          ZEME_USERS_ROLE.RENTER
        );
        if (response?.session_token) {
          setItem(ZEME_TEMP_SESSION_TOKEN, response.session_token);
        }
      }
      // Update phone state
      setPhone(values.phone);
      handleStepChange(REGISTER_STEPS.OTP);
    } catch (error) {
      handleApiError(
        error,
        ERROR_MESSAGES.failedToUpdatePhoneAndSendVerification
      );
      throw error;
    } finally {
      setIsUpdatePhoneLoading(false);
    }
  };

  const handleGoogleOAuth = async (
    token: string,
    userInfo: { name: string; email: string; picture: string }
  ) => {
    await withLoadingState(
      setIsLoading,
      async () => {
        const response = (await loginWithGoogle(token)) as Record<
          string,
          unknown
        >;
        await handleOAuthResponse(response, userInfo.email);
      },
      ERROR_MESSAGES.googleSignupFailed
    );
  };

  const handleAppleOAuth = async (
    token: string,
    userInfo: {
      name?: { firstName?: string; lastName?: string };
      email: string;
    }
  ) => {
    // Decode the Apple JWT token exactly like old flow
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = jwtDecode(token);
    const userEmail = res?.email || userInfo?.email || '';

    // Check for private relay email exactly like old flow
    if (res?.is_private_email) {
      showErrorMsg(VALIDATION_MESSAGES.applePrivateEmail);
      return;
    }

    // Check if email is available exactly like old flow
    if (!userEmail) {
      showErrorMsg(VALIDATION_MESSAGES.appleEmailNotFound);
      return;
    }

    await withLoadingState(
      setIsLoading,
      async () => {
        const response = (await loginWithApple(
          token,
          userInfo.name?.firstName || '',
          userInfo.name?.lastName || '',
          userEmail
        )) as Record<string, unknown>;
        await handleOAuthResponse(response, userEmail);
      },
      ERROR_MESSAGES.appleSignupFailed
    );
  };

  const handleOAuthComplete = () => {
    handleModalClose();
    if (pathname === APP_ROUTE.EXPLORE) {
      window?.location?.reload();
    } else {
      router.push(APP_ROUTE.EXPLORE);
    }
  };

  const handlePhoneSubmitAdapter = async (values: { phone: string }) => {
    const registerValues: RegisterFormValues = {
      phone: values.phone,
      code: '',
      userType: selectedUserType || 'renter',
      firstName: '',
      lastName: '',
      email: '',
      consent: false,
    };
    await handlePhoneSubmit(registerValues);
  };

  const handleStepChangeAdapter = (step: string) => {
    switch (step) {
      case LOGIN_STEPS.SMS:
        handleStepChange(REGISTER_STEPS.OTP);
        break;
      case LOGIN_STEPS.PHONE:
        handleStepChange(REGISTER_STEPS.PHONE);
        break;
      default:
        handleStepChange(step as RegisterStep);
        break;
    }
  };

  const handleGoogleOAuthAdapter = (
    token: string,
    userInfo: { name?: string; email?: string; picture?: string }
  ) => {
    if (userInfo.name && userInfo.email) {
      handleGoogleOAuth(token, {
        name: userInfo.name,
        email: userInfo.email,
        picture: userInfo.picture || '',
      });
    }
  };

  const handleAppleOAuthAdapter = (
    token: string,
    userInfo: {
      name?: { firstName?: string; lastName?: string };
      email?: string;
    }
  ) => {
    // Decode the Apple JWT token exactly like old flow
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = jwtDecode(token);
    const userEmail = res?.email || userInfo?.email || '';

    if (userEmail) {
      handleAppleOAuth(token, {
        name: userInfo.name,
        email: userEmail,
      });
    }
  };

  const handleUpdateEmailModalClose = () => {
    handleStepChange(REGISTER_STEPS.EMAIL_VERIFICATION);
  };

  const handleUpdatePhoneModalClose = () => {
    handleStepChange(REGISTER_STEPS.OTP);
  };

  const renderStep = () => {
    const commonProps = {
      onStepChange: handleStepChange,
      onClose: handleModalClose,
      phone,
      email,
      selectedUserType,
      canResendPhone,
      canResendEmail,
      onGoogleOAuth: handleGoogleOAuth,
      onAppleOAuth: handleAppleOAuth,
      isOAuthFlow,
      onOAuthComplete: handleOAuthComplete,
      onCropModalStateChange: setIsCropModalOpen,
      onOpenLoginModal,
    };

    switch (step) {
      case REGISTER_STEPS.USER_TYPE:
        return (
          <UserTypeStep
            {...commonProps}
            onUserTypeSelect={handleUserTypeSelect}
            isLoading={isLoading}
            userTypeError={userTypeError}
          />
        );
      case REGISTER_STEPS.PHONE:
        return (
          <PhoneStep
            onStepChange={handleStepChangeAdapter}
            onClose={handleModalClose}
            onPhoneSubmit={handlePhoneSubmitAdapter}
            isLoading={isLoading}
            title="Sign up"
            onGoogleOAuth={handleGoogleOAuthAdapter}
            onAppleOAuth={handleAppleOAuthAdapter}
            hideOAuth={
              selectedUserType === ZEME_USERS_ROLE.AGENT ||
              selectedUserType === ZEME_USERS_ROLE.LANDLORD
            }
            onOpenLoginModal={() => {
              handleModalClose();
              onOpenLoginModal?.();
            }}
          />
        );
      case REGISTER_STEPS.OTP:
        return (
          <OtpStep
            {...commonProps}
            onOtpSubmit={handleOtpSubmit}
            isOtpLoading={isOtpLoading}
            phoneResendTimer={phoneResendTimer}
            onPhoneResend={handlePhoneResend}
            hideTryAnotherNumber={isOAuthFlow && isVerifiedPhone}
          />
        );
      case REGISTER_STEPS.USER_DETAILS:
        return (
          <UserDetailsStep
            {...commonProps}
            onUserDetailsSubmit={handleUserDetailsSubmit}
            isUserDetailsLoading={isUserDetailsLoading}
            initialValues={userDetailsData || undefined}
            workDetailsData={workDetailsData || undefined}
            userDetailsError={userDetailsError}
          />
        );
      case REGISTER_STEPS.WORK_DETAILS:
        return (
          <WorkDetailsStep
            {...commonProps}
            savedCropState={savedCropState}
            setSavedCropState={setSavedCropState}
            onWorkDetailsSubmit={handleWorkDetailsSubmit}
            isWorkDetailsLoading={isWorkDetailsLoading}
            initialValues={workDetailsData || undefined}
            userDetailsData={userDetailsData || undefined}
            onSaveWorkDetails={handleSaveWorkDetails}
            workDetailsError={workDetailsError}
            setWorkDetailsError={setWorkDetailsError}
          />
        );
      case REGISTER_STEPS.EMAIL_VERIFICATION:
        return (
          <EmailVerificationStep
            {...commonProps}
            onEmailVerificationSubmit={handleEmailVerificationSubmit}
            isEmailVerificationLoading={isEmailVerificationLoading}
            emailResendTimer={emailResendTimer}
            onEmailResend={handleEmailResend}
          />
        );
      case REGISTER_STEPS.UPDATE_EMAIL:
        return (
          <UpdateEmailStep
            {...commonProps}
            onClose={handleUpdateEmailModalClose}
            onUpdateEmailSubmit={handleUpdateEmailSubmit}
            isUpdateEmailLoading={isUpdateEmailLoading}
            userDetailsData={userDetailsData || undefined}
          />
        );
      case REGISTER_STEPS.UPDATE_PHONE:
        return (
          <UpdatePhoneStep
            {...commonProps}
            onClose={handleUpdatePhoneModalClose}
            onUpdatePhoneSubmit={handleUpdatePhoneSubmit}
            isUpdatePhoneLoading={isUpdatePhoneLoading}
            userDetailsData={userDetailsData || undefined}
          />
        );
      default:
        return null;
    }
  };

  // Combined loading state for all steps
  const isAnyLoading =
    isLoading ||
    isOtpLoading ||
    isUserDetailsLoading ||
    isWorkDetailsLoading ||
    isEmailVerificationLoading ||
    isUpdateEmailLoading ||
    isUpdatePhoneLoading;

  const modalContent = (
    <>
      <Loader isVisible={isAnyLoading} />
      {isCropModalOpen && (
        <div className="fixed inset-0 z-[69] bg-black/20 backdrop-blur-[2px]" />
      )}
      <DialogContent
        size="md"
        showCloseButton={false}
        closeOnOutsideClick={true}
        className={`h-[100dvh] md:h-[790px] md:min-h-[790px] max-h-[100dvh] md:max-h-[790px] max-w-full md:max-w-[1236px] !p-0 !rounded-none md:!rounded-2xl overflow-hidden transition-all duration-300 ${
          isCropModalOpen ? 'blur-sm opacity-70' : ''
        }`}
        tabIndex={-1}
      >
        {renderStep()}
      </DialogContent>
    </>
  );

  if (trigger) {
    return (
      <Dialog open={isOpen} onOpenChange={handleModalClose}>
        <DialogOverlay className="" />
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        {modalContent}
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogOverlay className="" />
      {modalContent}
    </Dialog>
  );
};

export default RegisterModal;
